import type { ReactElement } from 'react';
import { GAMES, GAME_KEYS, totalCombinations } from '../engine';
import { formatDecimal, formatEuros, formatOdds, formatPercent } from '../lib/format';
import { SEED_COVERAGE } from '../data/seed';

/**
 * The page that keeps the rest of the app honest. Everything the method does and,
 * more importantly, everything it does not do.
 */
export function Method(): ReactElement {
  return (
    <div className="screen">
      <h1>Méthode</h1>
      <p className="lede">
        Ce que cette application fait, ce qu’elle ne fait pas, et les chiffres derrière.
      </p>

      <h2>Elle ne prédit rien</h2>
      <p className="small">
        Testé sur les trois jeux : l’ajustement à la loi uniforme n’est rejeté sur aucun,
        aucun numéro ne dépasse le seuil de Bonferroni, et l’autocorrélation d’un tirage au
        suivant est nulle. Les fréquences passées ne portent aucune information sur le
        tirage suivant, et <strong>le générateur ne les consulte jamais</strong>.
      </p>
      <p className="small dim">
        Instantané embarqué : {SEED_COVERAGE.loto.count} tirages Loto,{' '}
        {SEED_COVERAGE.em.count} EuroMillions, {SEED_COVERAGE.eurodreams.count} EuroDreams,
        depuis {SEED_COVERAGE.loto.from}. En ligne, l’application travaille sur
        l’historique complet servi par le serveur. Le détail des tests est sur l’onglet
        Statistiques.
      </p>

      <h2>Elle réduit le partage</h2>
      <p className="small">
        Sur un rang parimutuel, le lot est divisé entre tous les gagnants. Éviter les
        combinaisons que la foule joue — anniversaires, suites, lignes du bulletin, le 7,
        le 13 — réduit le nombre de co-gagnants attendus, donc augmente la part qui
        revient au gagnant. C’est la <em>seule</em> grandeur que la méthode déplace.
      </p>

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Jeu</th>
              <th>Lots</th>
              <th>Gain brut</th>
              <th>Gain réel</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Loto</td>
              <td>parimutuels</td>
              <td>+66 %</td>
              <td>+66 %</td>
            </tr>
            <tr>
              <td>EuroMillions</td>
              <td>parimutuels</td>
              <td>+25 %</td>
              <td>+25 %</td>
            </tr>
            <tr>
              <td>EuroDreams</td>
              <td>montants fixes</td>
              <td>+20 %</td>
              <td>+0,4 %</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p className="small dim">
        La dernière colonne est celle qui compte. EuroDreams verse une rente fixe à chaque
        gagnant du rang 1, jusqu’à trois simultanément : il n’y a presque rien à partager,
        donc presque rien à gagner à éviter les grilles populaires.
      </p>

      <div className="notice">
        <strong>Ces chiffres ont été revus à la baisse.</strong> Ils valaient +181 %,
        +135 % et +0,9 % tant que le modèle de popularité était supposé décrire
        exactement les joueurs français. Il a depuis été confronté aux rapports de gains
        publiés par la FDJ : le lien existe et il est net, mais son ampleur réelle vaut
        0,56 de ce que le modèle supposait au Loto, et 0,30 à l’EuroMillions. Les valeurs
        ci-dessus sont les corrigées.
      </div>

      <h2>Elle ne change pas la probabilité de gagner</h2>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Jeu</th>
              <th>Matrice</th>
              <th>Rang 1</th>
              <th>λ</th>
              <th>Prix</th>
            </tr>
          </thead>
          <tbody>
            {GAME_KEYS.map((key) => {
              const g = GAMES[key];
              return (
                <tr key={key}>
                  <td>{g.label}</td>
                  <td>
                    {g.k}/{g.n} + {g.bk}/{g.bmax}
                  </td>
                  <td>{formatOdds(totalCombinations(g))}</td>
                  <td>{formatDecimal(g.lambda, 3)}</td>
                  <td>{formatEuros(g.price)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="small dim">
        Ces cotes valent pour toute grille, y compris celles produites ici. λ est dérivé de
        l’exposition au partage du jeu, pas choisi : 1,6 quand tous les rangs sont des
        cagnottes, 0,032 quand les lots sont à montant fixe.
      </p>

      <h2>Taux de retour au joueur</h2>
      <div className="notice warn">
        <strong>{formatPercent(GAMES.loto.rtp ?? 0, 1)} au Loto, environ 50 % à
        l’EuroMillions.</strong> Chaque euro misé vaut statistiquement 50 à 55 centimes,
        quelle que soit la grille. La méthode améliore le partage, jamais le rendement.
        L’espérance de gain reste négative.
      </div>

      <h2>Deux règles d’usage</h2>
      <p className="small">
        <strong>Ne relancez pas une génération pour « avoir mieux ».</strong> Jouez la
        première grille sortie. Trier a posteriori réintroduit exactement le biais humain
        que le procédé élimine.
      </p>
      <p className="small">
        <strong>λ au-delà de 2,5 est contre-productif.</strong> Les grilles deviennent si
        systématiquement hautes qu’elles se remettent à ressembler à celles des autres
        joueurs appliquant la même logique.
      </p>

      <h2>Limites du modèle de popularité</h2>
      <p className="small">
        Les coefficients viennent de la littérature néerlandaise et britannique (Wang &amp;
        al. 2016, Simon 1998, Roger &amp; Broihanne 2007), pas de données françaises. Ils
        ont été confrontés aux rapports de gains de la FDJ : pour chaque tirage, le nombre
        de joueurs ayant trouvé tous les numéros est proportionnel à la fréquence à
        laquelle cette combinaison est jouée.
      </p>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Jeu</th>
              <th>Tirages</th>
              <th>Élasticité mesurée</th>
              <th>IC 95 %</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Loto</td>
              <td>1 112</td>
              <td>0,56</td>
              <td>0,50 – 0,62</td>
            </tr>
            <tr>
              <td>EuroMillions</td>
              <td>688</td>
              <td>0,30</td>
              <td>0,23 – 0,36</td>
            </tr>
            <tr>
              <td>EuroDreams</td>
              <td>296</td>
              <td>non mesurable</td>
              <td>−0,30 – 0,46</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p className="small dim">
        Une élasticité de 1 signifierait que le modèle décrit exactement les joueurs
        français. Les intervalles excluent 1 sur les deux jeux mesurables : le modèle voit
        juste sur le <em>sens</em>, et se trompe sur l’<em>ampleur</em>. Les grilles
        produites ne changent pas pour autant — classer les combinaisons par popularité
        donne le même ordre — seule la valeur annoncée du procédé change. EuroDreams ne
        totalise que 64 gagnants au rang « six numéros » depuis 2023 : trop peu pour
        conclure, l’estimation du Loto y est reprise faute de mieux.
      </p>

      <h2>D’où viennent les résultats</h2>
      <p className="small">
        Des archives officielles de la FDJ, une par jeu, qui contiennent chaque tirage et
        le rapport de gains de chaque rang. Le serveur les relit trois fois par jour, et
        aussi à l’ouverture de l’application quand un tirage attendu manque encore — c’est
        ce qui fait apparaître le résultat du soir sans attendre.
      </p>
      <p className="small dim">
        Aucun résultat n’est écrit sans avoir passé une validation complète : bon nombre de
        numéros, numéros distincts, dans les bornes du jeu, date réelle. Une ligne
        partiellement lue est journalisée et jetée, jamais enregistrée.
      </p>

      <h2>Ce que l’application ne fera pas</h2>
      <p className="small">
        Pas de numéros « chauds », « froids » ou « en retard ». Pas de pondération par les
        fréquences passées. Pas de score de confiance sur une grille. Pas de notification
        de tirage : iOS n’autorise pas de tâche de fond fiable pour une application web.
      </p>

      <p className="footnote">
        Usage strictement personnel. Jouer comporte des risques : endettement, isolement,
        dépendance. Si le jeu devient un problème, appelez le 09 74 75 13 13 (appel non
        surtaxé).
      </p>
    </div>
  );
}
